/**
 * Static hotel-floor visual, rendered once to an offscreen canvas and blitted
 * each frame. The arena is laid out as concentric zones — outer walnut wall,
 * meeting-room carpet ring, herringbone-corridor ring, marble lobby — with
 * fixed furniture sprinkled in to make it read as a real hotel from above.
 *
 * Pure decoration. No collision; the playable shape is still the circular arena.
 */

import { WORLD_RADIUS } from "./World";

const PADDING = 80; // canvas size buffer beyond the arena radius
const CANVAS_SIZE = (WORLD_RADIUS + PADDING) * 2;

const COLORS = {
  walnut: "#3d2a1d",
  walnutHighlight: "#5a3d28",
  meetingCarpet: "#5e1c2a",
  meetingDiamond: "#a07738",
  hallwayCarpet: "#1d3a3e",
  hallwayWeave: "#2a585e",
  hallwayWeaveLight: "#6fd6c8",
  // Dark walnut-marble — light enough to read as a polished lobby floor,
  // dark enough that the off-white dust bunnies and trash bags stay legible.
  marbleBase: "#3a2f22",
  marbleVein: "#7a6244",
  marbleGrout: "#241c14",
  furnitureWood: "#2a1e14",
  furnitureCloth: "#7a4a3a",
  furniturePlant: "#3a6a3a",
  furniturePlantPot: "#5a3a28",
} as const;

// Zone radii (from center outward).
const R_LOBBY = 320;
const R_HALLWAY = 680;
const R_MEETING = WORLD_RADIUS - 70;
const R_WALL_OUTER = WORLD_RADIUS;

export class FloorCache {
  /** Offscreen canvas centered on world origin. */
  private bitmap: HTMLCanvasElement | null = null;

  /** World coords of where the bitmap's top-left maps to. */
  private offsetX = -(WORLD_RADIUS + PADDING);
  private offsetY = -(WORLD_RADIUS + PADDING);

  ensure(): HTMLCanvasElement {
    if (this.bitmap) return this.bitmap;
    const c = document.createElement("canvas");
    c.width = CANVAS_SIZE;
    c.height = CANVAS_SIZE;
    const ctx = c.getContext("2d")!;
    // Draw in world coords centered.
    ctx.translate(WORLD_RADIUS + PADDING, WORLD_RADIUS + PADDING);
    this.paint(ctx);
    this.bitmap = c;
    return c;
  }

  /** Blit the cached floor into the active ctx. Caller has world-translate already applied. */
  draw(ctx: CanvasRenderingContext2D): void {
    const bmp = this.ensure();
    ctx.drawImage(bmp, this.offsetX, this.offsetY);
  }

  private paint(ctx: CanvasRenderingContext2D): void {
    this.paintWalnutWall(ctx);
    this.paintMeetingRoom(ctx);
    this.paintHallway(ctx);
    this.paintLobby(ctx);
    this.paintFurniture(ctx);
    this.paintZoneSeparators(ctx);
  }

  private paintWalnutWall(ctx: CanvasRenderingContext2D): void {
    // Outer walnut ring — the "walls" of the hotel viewed from above.
    ctx.fillStyle = COLORS.walnut;
    ctx.beginPath();
    ctx.arc(0, 0, R_WALL_OUTER, 0, Math.PI * 2);
    ctx.fill();
    // Wood-grain hint: faint radial streaks.
    ctx.strokeStyle = COLORS.walnutHighlight;
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.35;
    for (let i = 0; i < 80; i++) {
      const a = (i / 80) * Math.PI * 2;
      const r1 = R_MEETING + 6 + Math.random() * 6;
      const r2 = R_WALL_OUTER - 4 - Math.random() * 6;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r1, Math.sin(a) * r1);
      ctx.lineTo(Math.cos(a) * r2, Math.sin(a) * r2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  private paintMeetingRoom(ctx: CanvasRenderingContext2D): void {
    // Filled burgundy ring.
    ctx.fillStyle = COLORS.meetingCarpet;
    ctx.beginPath();
    ctx.arc(0, 0, R_MEETING, 0, Math.PI * 2);
    ctx.fill();
    // Carve out the inner area so this is truly a ring.
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(0, 0, R_HALLWAY, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";

    // Diamond/lattice pattern, masked to the ring.
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, R_MEETING, 0, Math.PI * 2);
    ctx.arc(0, 0, R_HALLWAY, 0, Math.PI * 2, true);
    ctx.clip("evenodd");

    ctx.strokeStyle = COLORS.meetingDiamond;
    ctx.lineWidth = 0.9;
    ctx.globalAlpha = 0.55;
    const step = 38;
    const extent = R_MEETING + step;
    ctx.beginPath();
    for (let x = -extent; x <= extent; x += step) {
      ctx.moveTo(x, -extent);
      ctx.lineTo(x + extent * 2, extent);
      ctx.moveTo(x, -extent);
      ctx.lineTo(x - extent * 2, extent);
    }
    ctx.stroke();

    // Tiny gold dots at lattice intersections for richness.
    ctx.fillStyle = COLORS.meetingDiamond;
    ctx.globalAlpha = 0.75;
    for (let x = -extent; x <= extent; x += step) {
      for (let y = -extent; y <= extent; y += step) {
        if (((x / step) + (y / step)) % 2 === 0) {
          ctx.beginPath();
          ctx.arc(x, y, 1.4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  private paintHallway(ctx: CanvasRenderingContext2D): void {
    // Carpet base.
    ctx.fillStyle = COLORS.hallwayCarpet;
    ctx.beginPath();
    ctx.arc(0, 0, R_HALLWAY, 0, Math.PI * 2);
    ctx.fill();
    // Carve lobby center out so this is a ring.
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(0, 0, R_LOBBY, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";

    // Herringbone — V-shaped chevrons. Matches the carpet in the real Rosie product photos.
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, R_HALLWAY, 0, Math.PI * 2);
    ctx.arc(0, 0, R_LOBBY, 0, Math.PI * 2, true);
    ctx.clip("evenodd");

    const tile = 22;
    const extent = R_HALLWAY + tile;
    ctx.strokeStyle = COLORS.hallwayWeave;
    ctx.lineWidth = 2.4;
    ctx.lineCap = "square";

    let row = 0;
    for (let y = -extent; y <= extent; y += tile) {
      const shift = (row % 2) * tile;
      for (let x = -extent + shift; x <= extent; x += tile * 2) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + tile, y + tile / 2);
        ctx.lineTo(x + tile * 2, y);
        ctx.stroke();
      }
      row++;
    }

    // Faint mint highlight running along one chevron family — adds depth and brand color.
    ctx.strokeStyle = COLORS.hallwayWeaveLight;
    ctx.lineWidth = 0.6;
    ctx.globalAlpha = 0.18;
    row = 0;
    for (let y = -extent; y <= extent; y += tile) {
      const shift = (row % 2) * tile;
      for (let x = -extent + shift; x <= extent; x += tile * 2) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + tile, y + tile / 2);
        ctx.stroke();
      }
      row++;
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private paintLobby(ctx: CanvasRenderingContext2D): void {
    // Marble base.
    ctx.fillStyle = COLORS.marbleBase;
    ctx.beginPath();
    ctx.arc(0, 0, R_LOBBY, 0, Math.PI * 2);
    ctx.fill();

    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, R_LOBBY, 0, Math.PI * 2);
    ctx.clip();

    // Tile grout grid — large square tiles, 80 units.
    ctx.strokeStyle = COLORS.marbleGrout;
    ctx.lineWidth = 1.2;
    ctx.globalAlpha = 0.5;
    const tile = 80;
    for (let x = -R_LOBBY; x <= R_LOBBY; x += tile) {
      ctx.beginPath();
      ctx.moveTo(x, -R_LOBBY);
      ctx.lineTo(x, R_LOBBY);
      ctx.stroke();
    }
    for (let y = -R_LOBBY; y <= R_LOBBY; y += tile) {
      ctx.beginPath();
      ctx.moveTo(-R_LOBBY, y);
      ctx.lineTo(R_LOBBY, y);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    // Marble vein swirls.
    ctx.strokeStyle = COLORS.marbleVein;
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = 0.5;
    for (let i = 0; i < 30; i++) {
      const x = (Math.random() - 0.5) * R_LOBBY * 1.6;
      const y = (Math.random() - 0.5) * R_LOBBY * 1.6;
      const len = 30 + Math.random() * 80;
      const angle = Math.random() * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.bezierCurveTo(
        x + Math.cos(angle) * len * 0.4,
        y + Math.sin(angle) * len * 0.4 + 8,
        x + Math.cos(angle) * len * 0.8,
        y + Math.sin(angle) * len * 0.8 - 8,
        x + Math.cos(angle) * len,
        y + Math.sin(angle) * len,
      );
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /** Sofas, tables, plants, reception desk — placed at fixed coords so the layout is consistent. */
  private paintFurniture(ctx: CanvasRenderingContext2D): void {
    // Reception desk in the lobby — long curved counter at the top.
    this.drawReceptionDesk(ctx, 0, -R_LOBBY + 60, 220, 30);

    // Four lobby seating clusters at the corners of the lobby tile area.
    const seatRadius = R_LOBBY - 90;
    for (let i = 0; i < 4; i++) {
      const a = Math.PI / 4 + (i * Math.PI) / 2;
      this.drawCoffeeTableWithChairs(
        ctx,
        Math.cos(a) * seatRadius,
        Math.sin(a) * seatRadius,
      );
    }

    // Conference tables in the meeting-room ring — 6 around the perimeter.
    const confR = (R_HALLWAY + R_MEETING) / 2;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
      const x = Math.cos(a) * confR;
      const y = Math.sin(a) * confR;
      this.drawConferenceTable(ctx, x, y, a + Math.PI / 2);
    }

    // Hallway plants at room boundaries (8 around the corridor).
    const plantR = (R_LOBBY + R_HALLWAY) / 2;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const x = Math.cos(a) * plantR;
      const y = Math.sin(a) * plantR;
      this.drawPlant(ctx, x, y);
    }
  }

  /** Faint hairline circles separating the zones — subtle, like room thresholds. */
  private paintZoneSeparators(ctx: CanvasRenderingContext2D): void {
    ctx.strokeStyle = "rgba(255,255,255,0.07)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(0, 0, R_LOBBY, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, R_HALLWAY, 0, Math.PI * 2);
    ctx.stroke();

    // Soft mint glow on the inner edge of the outer wall — the "baseboard light".
    const grad = ctx.createRadialGradient(0, 0, R_MEETING - 4, 0, 0, R_MEETING + 4);
    grad.addColorStop(0, "rgba(111,214,200,0)");
    grad.addColorStop(0.5, "rgba(111,214,200,0.4)");
    grad.addColorStop(1, "rgba(111,214,200,0)");
    ctx.fillStyle = grad;
    ctx.fillRect(-R_MEETING - 10, -R_MEETING - 10, (R_MEETING + 10) * 2, (R_MEETING + 10) * 2);
  }

  private drawReceptionDesk(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    w: number,
    h: number,
  ): void {
    ctx.save();
    ctx.translate(cx, cy);
    // shadow
    ctx.fillStyle = "rgba(0,0,0,0.25)";
    roundRect(ctx, -w / 2 + 2, -h / 2 + 3, w, h, 6);
    ctx.fill();
    // desk top
    ctx.fillStyle = COLORS.furnitureWood;
    roundRect(ctx, -w / 2, -h / 2, w, h, 6);
    ctx.fill();
    // mint pin-stripe along the front edge — Rosie brand cue
    ctx.fillStyle = "#6fd6c8";
    ctx.globalAlpha = 0.7;
    roundRect(ctx, -w / 2 + 8, h / 2 - 4, w - 16, 2, 1);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private drawCoffeeTableWithChairs(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
  ): void {
    ctx.save();
    ctx.translate(cx, cy);
    // four armchairs around a low table
    ctx.fillStyle = COLORS.furnitureCloth;
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * 36, Math.sin(a) * 36, 14, 0, Math.PI * 2);
      ctx.fill();
    }
    // table top (shadow)
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.beginPath();
    ctx.arc(1.5, 2, 22, 0, Math.PI * 2);
    ctx.fill();
    // table top
    ctx.fillStyle = COLORS.furnitureWood;
    ctx.beginPath();
    ctx.arc(0, 0, 20, 0, Math.PI * 2);
    ctx.fill();
    // tiny brass center
    ctx.fillStyle = "#a07738";
    ctx.beginPath();
    ctx.arc(0, 0, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawConferenceTable(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    rot: number,
  ): void {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    const tw = 90;
    const th = 36;
    // chairs along long sides
    ctx.fillStyle = "#1a1f26";
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.arc(i * 24, -th / 2 - 11, 9, 0, Math.PI * 2);
      ctx.arc(i * 24, th / 2 + 11, 9, 0, Math.PI * 2);
      ctx.fill();
    }
    // table shadow
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    roundRect(ctx, -tw / 2 + 2, -th / 2 + 2, tw, th, 6);
    ctx.fill();
    // table top
    ctx.fillStyle = COLORS.furnitureWood;
    roundRect(ctx, -tw / 2, -th / 2, tw, th, 6);
    ctx.fill();
    // table reflection
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    roundRect(ctx, -tw / 2 + 4, -th / 2 + 4, tw - 8, 4, 2);
    ctx.fill();
    ctx.restore();
  }

  private drawPlant(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
    ctx.save();
    ctx.translate(cx, cy);
    // shadow
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    ctx.beginPath();
    ctx.arc(1.5, 2, 16, 0, Math.PI * 2);
    ctx.fill();
    // pot
    ctx.fillStyle = COLORS.furniturePlantPot;
    ctx.beginPath();
    ctx.arc(0, 0, 13, 0, Math.PI * 2);
    ctx.fill();
    // leaves (clusters of green blobs)
    ctx.fillStyle = COLORS.furniturePlant;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * 7, Math.sin(a) * 7, 7, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = "#5a9a5a";
    ctx.beginPath();
    ctx.arc(0, 0, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
